/**
 * The reference user's access, imported into a request (21 Oct, Rusty): "see
 * what that user had and add to the new user, or take away, or just review".
 *
 * Pure (no React, no i18n), so node tests run it. One item of the checklist
 * (365FO, Bluestar, …, "Folders and sites") holds two kinds of entries:
 *
 * - entries on the system's role list (`role` set): kept means "in the role
 *   picker's selection", so keeping, removing and adding all go through the
 *   picks, and the picks are what the approvals follow, as before;
 * - everything else (folders, sites, CRM roles, text written on a request):
 *   kept means "in `choice.kept`"; folders and sites added from Approvers'
 *   list are in `choice.added`.
 *
 * Granted and live entries are kept by default; ones still requested, or a
 * folder the person only owns, are offered unticked.
 */

import type { RoleChoice, RoleSelection } from "./access";

/** A reference entry's pick: the area, its level, and (2 Nov) its 365FO sub-role. */
export interface ReferenceRole {
  id: string;
  level?: string | null;
  subroles?: string[] | null;
}

/** The entry shape these helpers read (the generated `ReferenceAccessItem`). */
export interface ReferenceItemLike {
  id: string;
  label: string;
  kind?: string;
  role?: ReferenceRole | null;
  relation?: string;
}

/** The card shape these helpers read (the generated `ReferenceAccessSystem`). */
export interface ReferenceSystemLike {
  key: string;
  item_key?: string | null;
  items?: ReferenceItemLike[];
  add_options?: { id: string; label: string }[];
}

/** A role list as the diff needs it, for an added pick's label. */
export interface RoleListLike {
  levels?: string[];
  options?: { id: string; label: string }[];
}

/** What the requester did with one checklist item's reference entries. */
export interface ReferenceChoice {
  /** "Copy into this request" was pressed for this item. */
  applied: boolean;
  /** Ids of entries without a role pick that are kept. */
  kept: string[];
  /** Ids of folders or sites added from Approvers' list. */
  added: string[];
}

export const EMPTY_CHOICE: ReferenceChoice = { applied: false, kept: [], added: [] };

/** Kept unless the person unticks it: granted on a request, or live in the system. */
export function keptByDefault(item: ReferenceItemLike): boolean {
  return item.relation === undefined || item.relation === "granted" || item.relation === "assigned";
}

/** The cards one checklist item covers ("shared_folders": folders and SharePoint). */
export function systemsForItem<T extends ReferenceSystemLike>(
  systems: readonly T[],
  itemKey: string,
): T[] {
  return systems.filter((system) => system.item_key === itemKey);
}

/** Every entry of these cards (a generic "has access" one only when nothing else is). */
export function entriesOf(systems: readonly ReferenceSystemLike[]): ReferenceItemLike[] {
  return systems.flatMap((system) => system.items ?? []);
}

function sameChoice(choice: RoleChoice, role: ReferenceRole): boolean {
  return choice.id === role.id && (role.level ? (choice.level ?? null) === role.level : true);
}

/** Whether a pick covers the entry: the area, and (2 Nov) its 365FO sub-roles ticked. */
function covers(choice: RoleChoice, role: ReferenceRole): boolean {
  const held = choice.subroles ?? [];
  return sameChoice(choice, role) && (role.subroles ?? []).every((name) => held.includes(name));
}

/** Whether an entry is kept now. */
export function isKept(
  item: ReferenceItemLike,
  selection: RoleSelection,
  choice: ReferenceChoice,
): boolean {
  const role = item.role;
  if (role) {
    return selection.choices.some((picked) => covers(picked, role));
  }
  return choice.kept.includes(item.id);
}

/** Keep or take away one entry. */
export function setKept(
  item: ReferenceItemLike,
  keep: boolean,
  selection: RoleSelection,
  choice: ReferenceChoice,
): { selection: RoleSelection; choice: ReferenceChoice } {
  const role = item.role;
  if (role && (role.subroles ?? []).length > 0) {
    // 2 Nov: a live 365FO role is one sub-role of its area; the area stays
    // while another of its sub-roles is still ticked.
    const names = role.subroles ?? [];
    const current = selection.choices.find((picked) => sameChoice(picked, role));
    const others = selection.choices.filter((picked) => picked !== current);
    const held = (current?.subroles ?? []).filter((name) => !names.includes(name));
    const subroles = keep ? [...held, ...names] : held;
    const choices =
      subroles.length > 0
        ? [...others, { ...current, id: role.id, level: role.level ?? null, subroles }]
        : others;
    return { selection: { ...selection, choices }, choice };
  }
  if (role) {
    const others = selection.choices.filter((picked) => !sameChoice(picked, role));
    const choices = keep ? [...others, { id: role.id, level: role.level ?? null }] : others;
    return { selection: { ...selection, choices }, choice };
  }
  const kept = choice.kept.filter((id) => id !== item.id);
  return { selection, choice: { ...choice, kept: keep ? [...kept, item.id] : kept } };
}

/**
 * "Copy into this request": the default-kept entries are kept, on top of
 * whatever was already picked (nothing the person chose is undone).
 */
export function applyReference(
  systems: readonly ReferenceSystemLike[],
  selection: RoleSelection,
  choice: ReferenceChoice,
): { selection: RoleSelection; choice: ReferenceChoice } {
  let next = { selection, choice: { ...choice, applied: true } };
  for (const item of entriesOf(systems)) {
    if (keptByDefault(item) && !isKept(item, next.selection, next.choice)) {
      next = setKept(item, true, next.selection, next.choice);
    }
  }
  return next;
}

/** Add or take back a folder or site from Approvers' list. */
export function setAdded(choice: ReferenceChoice, id: string, add: boolean): ReferenceChoice {
  const added = choice.added.filter((known) => known !== id);
  return { ...choice, added: add ? [...added, id] : added };
}

/** Kept, removed and added, as labels people read (and the note writes). */
export interface ReferenceDiff {
  kept: string[];
  removed: string[];
  added: string[];
}

export const EMPTY_DIFF: ReferenceDiff = { kept: [], removed: [], added: [] };

/** "Finance", "CFG – View": a pick as the picker names it. */
export function pickLabel(list: RoleListLike | undefined, picked: RoleChoice): string {
  const option = (list?.options ?? []).find((o) => o.id === picked.id);
  const label = option?.label ?? picked.id;
  return picked.level ? `${label} – ${picked.level}` : label;
}

/**
 * The difference with the reference user: kept and removed among their
 * entries (one only offered and left unticked is neither), and added: picks
 * that are none of theirs, and folders or sites added from the list.
 * `genericLabel` names an entry that is "access, nothing more said".
 */
export function referenceDiff(
  systems: readonly ReferenceSystemLike[],
  selection: RoleSelection,
  choice: ReferenceChoice,
  list: RoleListLike | undefined,
  genericLabel: string,
): ReferenceDiff {
  if (!choice.applied) {
    return EMPTY_DIFF;
  }
  const entries = entriesOf(systems);
  const label = (item: ReferenceItemLike) => (item.label === "" ? genericLabel : item.label);
  const kept: string[] = [];
  const removed: string[] = [];
  for (const item of entries) {
    if (isKept(item, selection, choice)) {
      kept.push(label(item));
    } else if (keptByDefault(item)) {
      removed.push(label(item));
    }
  }
  const theirs = entries.flatMap((item) => (item.role ? [item.role] : []));
  const added = selection.choices
    .filter((picked) => !theirs.some((role) => sameChoice(picked, role)))
    .map((picked) => pickLabel(list, picked));
  const options = systems.flatMap((system) => system.add_options ?? []);
  for (const id of choice.added) {
    const option = options.find((o) => o.id === id);
    if (option && !entries.some((item) => item.id === id)) {
      added.push(option.label);
    }
  }
  return {
    kept: [...new Set(kept)],
    removed: [...new Set(removed)],
    added: [...new Set(added)],
  };
}

/** Whether the diff says anything at all. */
export function hasDiff(diff: ReferenceDiff): boolean {
  return diff.kept.length + diff.removed.length + diff.added.length > 0;
}

/**
 * The folders and sites kept and added, as the Folder item's resource text
 * ("SEPA DK; Finance"), for a new hire whose resource is still empty.
 */
export function resourceText(
  systems: readonly ReferenceSystemLike[],
  choice: ReferenceChoice,
): string {
  const entries = entriesOf(systems).filter((item) => item.kind === "resource");
  const kept = entries.filter((item) => choice.kept.includes(item.id)).map((item) => item.label);
  const options = systems.flatMap((system) => system.add_options ?? []);
  const added = choice.added.flatMap((id) =>
    options.filter((o) => o.id === id).map((o) => o.label),
  );
  return [...new Set([...kept, ...added])].join("; ");
}

/** Approvers' folders or sites matching a search, not yet added, at most `limit`. */
export function matchingOptions<T extends { id: string; label: string; detail?: string | null }>(
  options: readonly T[],
  query: string,
  exclude: readonly string[],
  limit = 8,
): T[] {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return [];
  }
  return options
    .filter((option) => !exclude.includes(option.id))
    .filter((option) => {
      const text = `${option.label} ${option.detail ?? ""}`.toLocaleLowerCase();
      return words.every((word) => text.includes(word));
    })
    .slice(0, limit);
}
